import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Row, Col, Card } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import FormTextarea from '../../../components/form/FormTextarea';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexAiAgentSettingsPage from './CallexAiAgentSettingsPage';
import CallexLoader from '../../../components/CallexLoader';
import FormNumberInput from '../../../components/form/FormNumberInput';
import FormFileUpload from '../../../components/form/FormFileUpload';

import {
  callexAiAgentAdd,
  callexAiAgentUpdate,
  callexAiAgentDetailsGetById,
  callexVoiceListGet,
  useCallexVoiceList
} from '../../../store/slices/tenantSlices/configurationSlice';

// ---------------- OPTIONS ----------------

export const STATUS_OPTIONS = [
  { label: 'Draft (Testing)', value: 'draft' },
  { label: 'Active (Production)', value: 'active' },
  { label: 'Paused', value: 'paused' }
];

export const LANGUAGE_OPTIONS = [
  { label: 'English (US)', value: 'en-US' },
  // { label: 'English (UK)', value: 'en-GB' },
  // { label: 'Spanish', value: 'es-ES' },
  // { label: 'French', value: 'fr-FR' },
  // { label: 'German', value: 'de-DE' },
  { label: 'Hindi', value: 'hi-IN' },
  { label: 'Gujarati', value: 'gu-IN' }
];

const MODEL_STT_OPTIONS = [
  { label: 'Callex-1.1 (General Accents)', value: 'callex-1.1' },
  { label: 'Callex-1.2 (Thick Accents / Code-switching)', value: 'callex-1.2' },
  { label: 'Callex-1.3 (Global Multilingual)', value: 'callex-1.3' }
];

export const SPEAKING_STYLE_OPTIONS = [
  { label: 'Professional / Corporate', value: 'professional' },
  { label: 'Friendly / Casual', value: 'friendly' },
  { label: 'Urgent / Fast-paced', value: 'urgent' },
  { label: 'Empathetic / Soft', value: 'empathetic' }
];

const BACKGROUND_NOISE_VOLUME_OPTIONS = [
  { label: 'Completely Muted (Turns off background noise entirely)', value: 0.0 },
  { label: 'Normal Quiet', value: 0.05 },
  { label: 'Medium Quiet', value: 0.07 },
  { label: 'High Quiet', value: 0.1 },
  { label: 'Normal/Default (Subtle call center ambiance)', value: 0.2 },
  { label: 'Loud', value: 0.5 },
  { label: 'Max Volume', value: 1.0 }
];

const BARGE_IN_MODE_OPTIONS = [
  { label: 'Disabled (Do not interrupt bot)', value: 'disabled' },
  { label: 'Polite (Only interrupt on full sentences)', value: 'polite' },
  { label: 'Balanced (Interrupt on 3+ words)', value: 'balanced' },
  { label: 'Aggressive (Interrupt instantly on noise)', value: 'aggressive' }
];

const VOICEMAIL_LOGIC_OPTIONS = [
  { label: 'Instantly Hang up', value: 'hangup' },
  { label: 'Leave dynamic voice message', value: 'leave_message' },
  { label: 'Wait for human operator', value: 'human_escalate' }
];

const OBJECTION_HANDLING_OPTIONS = [
  { label: 'Standard', value: 'standard' },
  { label: 'Persistent', value: 'persistent' }
];

export const ANALYSIS_TYPE_OPTIONS = [
  { label: 'String', value: 'string' },
  { label: 'Number', value: 'number' },
  { label: 'Boolean', value: 'boolean' }
];

const AddEditCallexAiAgent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const isEdit = Boolean(agentId);
  const callexVoiceList = useCallexVoiceList();
  const [loading, setLoading] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);

  const initialValues = {
    // Core
    name: '',
    description: '',
    systemPrompt: '',
    openingLine: '',
    voice: 'alloy',
    language: 'en-US',
    status: 'draft',
    file: null,

    // LLM
    temperature: 0.7,
    maxTokens: 250,
    sttEngine: 'callex-1.1',
    llmModel: 'callex-1.3',

    // Voice & Speech
    prosodyRate: 1.0,
    prosodyPitch: 1.0,
    fillerPhrases: ['Let me check...'],
    speakingStyle: 'professional',
    backgroundNoiseVolume: 0.2,

    // Call Behavior
    bargeInMode: 'balanced',
    patienceMs: 800,
    maxDuration: 30,
    ringTimeout: 30,
    voicemailLogic: 'hangup',
    fallbackMessage: "I'm sorry...",

    // Advanced
    sentimentRouting: false,
    piiRedaction: true,
    emotionalMirroring: true,
    dynamicCodeSwitching: true,
    objectionHandling: 'standard',
    autoSummary: true,
    autoSentiment: true,
    recordCall: true,
    webhookUrl: '',
    autoFollowUp: true,
    enableNLP: true,
    voiceSpeed: 1.0,

    // Analysis
    analysisSchema: [{ name: '', description: '', type: 'string' }]
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    temperature: Yup.number().min(0).max(1),
    maxTokens: Yup.number().integer(),
    prosodyRate: Yup.number(),
    prosodyPitch: Yup.number(),
    patienceMs: Yup.number().integer(),
    maxDuration: Yup.number().integer(),
    ringTimeout: Yup.number().integer(),
    webhookUrl: Yup.string().url('Invalid URL').nullable(),
    voiceSpeed: Yup.number().min(0, 'Min speed is 0').max(10, 'Max speed is 10'),
    backgroundNoiseVolume: Yup.number().min(0).max(1)
  });

  const onSubmit = async (vals) => {
    setLoading(true);
    try {
      if (isEdit) {
        const payload = {
          ...vals,
          id: agentId
        };
        await dispatch(callexAiAgentUpdate(payload)).unwrap();
        toast.success('Agent updated successfully.');
      } else {
        const formData = new FormData();
        Object.keys(vals).forEach((key) => {
          if (key === 'analysisSchema' || key === 'fillerPhrases') {
            formData.append(key, JSON.stringify(vals[key]));
          } else if (key === 'file') {
            if (vals[key]) {
              formData.append('file', vals[key]);
            }
          } else {
            formData.append(key, vals[key]);
          }
        });
        await dispatch(callexAiAgentAdd(formData)).unwrap();
        toast.success('Agent successfully created.');
      }
      navigate('/ai-callex/ai/callex-ai-agents');
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    setValues,
    validateForm
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchVoices = async () => {
    try {
      await dispatch(callexVoiceListGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchVoices();
  }, []);

  useEffect(() => {
    if (isEdit && agentId) {
      setIsFetching(true);
      dispatch(callexAiAgentDetailsGetById(agentId))
        .unwrap()
        .then((res) => {
          if (res?.data) {
            const data = res.data;
            let parsedFiller = Array.isArray(initialValues.fillerPhrases)
              ? initialValues.fillerPhrases
              : [];
            try {
              if (data.fillerPhrases) {
                if (typeof data.fillerPhrases === 'string') {
                  const parsed = JSON.parse(data.fillerPhrases);
                  if (Array.isArray(parsed)) {
                    parsedFiller = parsed;
                  }
                } else if (Array.isArray(data.fillerPhrases)) {
                  parsedFiller = data.fillerPhrases;
                }
              }
            } catch (error) {
              console.error('Error parsing filler phrases', error);
            }

            const filteredData = Object.keys(initialValues).reduce((acc, key) => {
              if (Object.prototype.hasOwnProperty.call(data, key)) {
                acc[key] = data[key];
              }
              return acc;
            }, {});

            let parsedAnalysis = Array.isArray(initialValues.analysisSchema)
              ? initialValues.analysisSchema
              : [];
            try {
              if (data.analysisSchema) {
                if (typeof data.analysisSchema === 'string') {
                  const parsed = JSON.parse(data.analysisSchema);
                  if (Array.isArray(parsed)) {
                    parsedAnalysis = parsed;
                  }
                } else if (Array.isArray(data.analysisSchema)) {
                  parsedAnalysis = data.analysisSchema;
                }
              }
            } catch (error) {
              console.error('Error parsing analysis schema', error);
            }

            setValues({
              ...initialValues,
              ...filteredData,
              fillerPhrases: parsedFiller,
              analysisSchema: parsedAnalysis,
              webhookUrl: data.webhookUrl || ''
            });
          }
        })
        .finally(() => setIsFetching(false));
    }
  }, [isEdit, agentId]);

  const addFillerPhrase = () => {
    setFieldValue('fillerPhrases', [...values.fillerPhrases, '']);
  };

  const removeFillerPhrase = (index) => {
    const updated = values.fillerPhrases.filter((_, i) => i !== index);
    setFieldValue('fillerPhrases', updated);
  };

  const addAnalysisField = () => {
    setFieldValue('analysisSchema', [
      ...values.analysisSchema,
      { name: '', description: '', type: 'string' }
    ]);
  };

  const removeAnalysisField = (index) => {
    const updated = values.analysisSchema.filter((_, i) => i !== index);
    setFieldValue('analysisSchema', updated);
  };

  return (
    <React.Fragment>
      <AdminHeader
        title={isEdit ? 'Edit AI Agent' : 'Create AI Agent'}
        backBtnName="Back"
        addBtnName={isEdit ? 'Update Agent' : 'Create Agent'}
        isBack
        isAdd
        onBackClick={() => navigate('/ai-callex/ai/callex-ai-agents')}
        form_id="callex-ai-agent-form"
        isLoading={loading}
        isAddDisabled={isFetching}
      />
      {isFetching ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-3"
          style={{ height: 'calc(100vh - 200px)' }}>
          <CallexLoader />
        </Card>
      ) : (
        <CallexAiAgentSettingsPage handleSubmit={handleSubmit} validateForm={validateForm}>
          {/* CORE SECTION */}
          <div id="core" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="4">
                  <FormInput
                    name="name"
                    label="Agent Name"
                    placeholder="Enter Agent Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="status"
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="language"
                    label="Agent Language"
                    options={LANGUAGE_OPTIONS}
                    value={values.language}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="openingLine"
                    label="Opening Line"
                    placeholder="First sentence on call"
                    value={values.openingLine}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="description"
                    label="Description"
                    placeholder="Enter Description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="12">
                  <FormTextarea
                    name="systemPrompt"
                    label='Train your Script ("System Prompt")'
                    placeholder="LLM instructions"
                    value={values.systemPrompt}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    rows={4}
                  />
                </Col>
                <Col lg="12">
                  <FormFileUpload
                    name="file"
                    label="Document Upload"
                    placeholder="Select Document"
                    className="table-select"
                    value={values.file}
                    setFieldValue={setFieldValue}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* LLM SECTION */}
          <div id="llm" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="6">
                  <FormNumberInput
                    name="temperature"
                    label="Temperature"
                    placeholder="0.0 - 1.0"
                    value={values.temperature}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    step="0.1"
                  />
                </Col>
                <Col lg="6">
                  <FormNumberInput
                    name="maxTokens"
                    label="Max Tokens"
                    placeholder="e.g. 250"
                    value={values.maxTokens}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="sttEngine"
                    label="STT Engine"
                    options={MODEL_STT_OPTIONS}
                    value={values.sttEngine}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="llmModel"
                    label="LLM Model"
                    options={MODEL_STT_OPTIONS}
                    value={values.llmModel}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* VOICE & SPEECH SECTION */}
          <div id="voice_speech" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="6">
                  <FormSelect
                    name="voice"
                    label="Voice"
                    placeholder="Select Voice"
                    options={callexVoiceList}
                    value={values.voice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="speakingStyle"
                    label="Speaking Style"
                    options={SPEAKING_STYLE_OPTIONS}
                    value={values.speakingStyle}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                {/* <Col lg="6">
                <FormNumberInput
                  name="prosodyRate"
                  label="Prosody Rate"
                  placeholder="1.0"
                  value={values.prosodyRate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  step="0.1"
                />
              </Col>
              <Col lg="6">
                <FormNumberInput
                  name="prosodyPitch"
                  label="Prosody Pitch"
                  placeholder="1.0"
                  value={values.prosodyPitch}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  step="0.1"
                />
              </Col> */}
                <Col lg="6">
                  <FormSelect
                    name="backgroundNoiseVolume"
                    label="Background Noise Volume"
                    options={BACKGROUND_NOISE_VOLUME_OPTIONS}
                    value={values.backgroundNoiseVolume}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="enableNLP"
                    label="Enable NLP"
                    value={String(values.enableNLP)}
                    onChange={() => setFieldValue('enableNLP', !values.enableNLP)}
                  />
                </Col>
                <Col lg="6">
                  <FormNumberInput
                    name="voiceSpeed"
                    label="Voice Speed"
                    placeholder="1.0"
                    value={values.voiceSpeed}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    step="0.1"
                  />
                </Col>
                <Col lg="12">
                  <label className="form-label mt-2">Filler Phrases</label>
                  <Row>
                    {Array.isArray(values.fillerPhrases) &&
                      values.fillerPhrases.map((phrase, index) => (
                        <Col lg="4" key={index} className="mb-3">
                          <div className="d-flex align-items-center gap-2">
                            <FormInput
                              name={`fillerPhrases[${index}]`}
                              placeholder="Add filler phrase"
                              value={phrase}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                              className="mb-0 flex-grow-1"
                            />
                            <div className="d-flex gap-1">
                              {values.fillerPhrases.length > 1 && (
                                <div
                                  className="icon-action danger"
                                  onClick={() => removeFillerPhrase(index)}>
                                  <Icon.Trash size={14} />
                                </div>
                              )}
                              {index === values.fillerPhrases.length - 1 && (
                                <div className="icon-action info" onClick={addFillerPhrase}>
                                  <Icon.Plus size={14} />
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      ))}
                  </Row>
                </Col>
              </Row>
            </div>
          </div>

          {/* CALL BEHAVIOR SECTION */}
          <div id="call_behavior" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="6">
                  <FormSelect
                    name="bargeInMode"
                    label="Barge-in / Interruption Mode"
                    options={BARGE_IN_MODE_OPTIONS}
                    value={values.bargeInMode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormNumberInput
                    name="patienceMs"
                    label="Patience (ms)"
                    placeholder="e.g. 800"
                    value={values.patienceMs}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormNumberInput
                    name="maxDuration"
                    label="Max Duration (min)"
                    placeholder="e.g. 30"
                    value={values.maxDuration}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormNumberInput
                    name="ringTimeout"
                    label="Ring Timeout (sec)"
                    placeholder="e.g. 30"
                    value={values.ringTimeout}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="voicemailLogic"
                    label="Voicemail Detection Protocol"
                    options={VOICEMAIL_LOGIC_OPTIONS}
                    value={values.voicemailLogic}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="12">
                  <FormInput
                    name="fallbackMessage"
                    label="Fallback Message"
                    placeholder="I'm sorry..."
                    value={values.fallbackMessage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* ANALYSIS SCHEMA SECTION */}
          <div id="analysis_schema" className="agent-section">
            <div className="context-card">
              <label className="form-label mb-3 fw-bold">Analysis Schema</label>

              <Row className="g-2">
                {!Array.isArray(values.analysisSchema) || values.analysisSchema.length === 0 ? (
                  <Col lg="12" className="text-center p-4 border rounded bg-light">
                    <div className="d-flex flex-column align-items-center">
                      <span className="text-muted small mb-2">No extraction fields defined.</span>
                      <div className="icon-action info" onClick={addAnalysisField}>
                        <Icon.Plus size={16} />
                      </div>
                    </div>
                  </Col>
                ) : (
                  values.analysisSchema.map((item, index) => (
                    <Col lg="12" key={index} className="mb-2 pb-2 border-bottom">
                      <Row className="align-items-center g-2">
                        <Col lg="3">
                          <FormInput
                            name={`analysisSchema[${index}].name`}
                            label="Field Name"
                            placeholder="Enter field name"
                            value={item.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            touched={touched}
                            errors={errors}
                          />
                        </Col>
                        <Col lg="3">
                          <FormSelect
                            name={`analysisSchema[${index}].type`}
                            label="Data Type"
                            placeholder="select data type"
                            options={ANALYSIS_TYPE_OPTIONS}
                            value={item.type}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            touched={touched}
                            errors={errors}
                          />
                        </Col>
                        <Col lg="5">
                          <FormInput
                            name={`analysisSchema[${index}].description`}
                            label="Description"
                            placeholder="Enter description"
                            value={item.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            touched={touched}
                            errors={errors}
                          />
                        </Col>
                        <Col lg="1" className="pb-1">
                          <div className="d-flex gap-1 justify-content-center pt-3">
                            {values.analysisSchema.length > 1 && (
                              <div
                                className="icon-action danger"
                                onClick={() => removeAnalysisField(index)}>
                                <Icon.Trash size={14} />
                              </div>
                            )}
                            {index === values.analysisSchema.length - 1 && (
                              <div className="icon-action info" onClick={addAnalysisField}>
                                <Icon.Plus size={14} />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  ))
                )}
              </Row>
            </div>
          </div>

          {/* ADVANCED SECTION */}
          <div id="advanced" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="6">
                  <FormToggle
                    name="sentimentRouting"
                    label="Sentiment Routing"
                    value={String(values.sentimentRouting)}
                    onChange={() => setFieldValue('sentimentRouting', !values.sentimentRouting)}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="piiRedaction"
                    label="PII Redaction"
                    value={String(values.piiRedaction)}
                    onChange={() => setFieldValue('piiRedaction', !values.piiRedaction)}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="emotionalMirroring"
                    label="Emotional Mirroring"
                    value={String(values.emotionalMirroring)}
                    onChange={() => setFieldValue('emotionalMirroring', !values.emotionalMirroring)}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="dynamicCodeSwitching"
                    label="Dynamic Code Switching"
                    value={String(values.dynamicCodeSwitching)}
                    onChange={() =>
                      setFieldValue('dynamicCodeSwitching', !values.dynamicCodeSwitching)
                    }
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="autoSummary"
                    label="Auto Summary"
                    value={String(values.autoSummary)}
                    onChange={() => setFieldValue('autoSummary', !values.autoSummary)}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="autoSentiment"
                    label="Auto Sentiment"
                    value={String(values.autoSentiment)}
                    onChange={() => setFieldValue('autoSentiment', !values.autoSentiment)}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="recordCall"
                    label="Record Call"
                    value={String(values.recordCall)}
                    onChange={() => setFieldValue('recordCall', !values.recordCall)}
                  />
                </Col>
                <Col lg="6">
                  <FormToggle
                    name="autoFollowUp"
                    label="Auto Follow Up"
                    value={String(values.autoFollowUp)}
                    onChange={() => setFieldValue('autoFollowUp', !values.autoFollowUp)}
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="objectionHandling"
                    label="Objection Handling"
                    options={OBJECTION_HANDLING_OPTIONS}
                    value={values.objectionHandling}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="12">
                  <FormInput
                    name="webhookUrl"
                    label="Webhook URL"
                    placeholder="https://example.com/webhook"
                    value={values.webhookUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </CallexAiAgentSettingsPage>
      )}
    </React.Fragment>
  );
};

export default AddEditCallexAiAgent;
